/**
 * Profile Page Component (TASK-135)
 *
 * User profile page displaying:
 * - User information (name, email, role)
 * - Organization info
 * - Bot settings (mode, personal instructions)
 * - Change password functionality
 * - Logout option
 */
import { useEffect, useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  User,
  Mail,
  Building2,
  Shield,
  LogOut,
  Key,
  Loader2,
  Save,
  MessageCircle,
} from 'lucide-react';
import { PageLayout } from '@/components/layout/PageLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import apiClient from '@/services/api/client';
import { usersApi } from '@/services/api/users';
import { useAccounts } from '@/hooks/useAccounts';
import { ChannelsPanel } from '@/components/users/ChannelsPanel';
import { useCanCallApi } from '@/hooks/useApiAccess';
import { ANY_UUID } from '@/lib/managerAccess';

interface UserProfile {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  role_display?: string;
  is_active: boolean;
  organization?: {
    id: string;
    name: string;
  };
  created_at?: string;
  last_login?: string;
}

const fetchCurrentUser = async (): Promise<UserProfile> => {
  const response = await apiClient.get<UserProfile>('/api/v1/auth/me/');
  return response.data;
};

const updateProfile = async (data: {
  first_name: string;
  last_name: string;
}): Promise<UserProfile> => {
  const response = await apiClient.patch<UserProfile>('/api/v1/auth/me/', data);
  return response.data;
};

const changePassword = async (data: {
  current_password: string;
  new_password: string;
}): Promise<void> => {
  await apiClient.post('/api/v1/auth/change-password/', data);
};

function getRoleBadgeVariant(role: string): 'default' | 'secondary' | 'outline' {
  switch (role) {
    case 'OWNER':
      return 'default';
    default:
      return 'outline';
  }
}

function getRoleDisplayName(role: string): string {
  switch (role) {
    case 'OWNER':
      return 'Владелец';
    case 'USER':
      return 'Пользователь';
    default:
      return role;
  }
}

export function ProfilePage() {

  // Profile editing state
  const [isEditing, setIsEditing] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');

  // Password change state
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState<string | null>(null);

  useEffect(() => {
    document.title = 'Профиль - SMETA AI';
  }, []);

  // Fetch current user
  const {
    data: user,
    isLoading: isUserLoading,
    isError: isUserError,
    refetch: refetchUser,
  } = useQuery({
    queryKey: ['currentUser'],
    queryFn: fetchCurrentUser,
  });

  // Fetch the full user (with channels) for the "Мои каналы" section.
  // /auth/me/ returns a lean profile without channels/template, so we fetch
  // the complete user record by id once it is known.
  // KAN-811: the user record is closed to the MANAGER role (no channels): no section.
  const canSeeChannels = useCanCallApi('GET', `/api/v1/users/${user?.id ?? ANY_UUID}/`);
  const { data: fullUser, isLoading: isChannelsLoading } = useQuery({
    queryKey: ['currentUserChannels', user?.id],
    queryFn: () => usersApi.getById(user!.id),
    enabled: !!user?.id && canSeeChannels,
  });

  // Initialize form data
  useEffect(() => {
    if (user) {
      setFirstName(user.first_name);
      setLastName(user.last_name);
    }
  }, [user]);

  // Profile update mutation
  const updateMutation = useMutation({
    mutationFn: updateProfile,
    onSuccess: () => {
      refetchUser();
      setIsEditing(false);
      showSuccessToast('Профиль обновлён');
    },
    onError: () => {
      toast.error('Не удалось сохранить профиль. Попробуйте позже.');
    },
  });

  // Password change mutation
  const passwordMutation = useMutation({
    mutationFn: changePassword,
    onSuccess: () => {
      setShowPasswordDialog(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setPasswordError(null);
      showSuccessToast('Пароль успешно изменён');
    },
    onError: () => {
      setPasswordError('Не удалось изменить пароль. Проверьте текущий пароль.');
    },
  });

  const showSuccessToast = (message: string) => {
    toast.success(message);
  };

  const handleSaveProfile = () => {
    updateMutation.mutate({ first_name: firstName, last_name: lastName });
  };

  const handleChangePassword = () => {
    setPasswordError(null);

    if (newPassword.length < 8) {
      setPasswordError('Пароль должен быть не менее 8 символов');
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('Пароли не совпадают');
      return;
    }

    passwordMutation.mutate({
      current_password: currentPassword,
      new_password: newPassword,
    });
  };

  // KAN-925: выход из текущего аккаунта; остальные вошедшие остаются (переход на следующий или на вход).
  const { logout: handleLogout } = useAccounts();

  const isLoading = isUserLoading;

  if (isLoading) {
    return (
      <PageLayout
        title="Профиль"
        titleIcon={
          <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center flex-shrink-0">
            <User className="h-5 w-5 text-white" />
          </div>
        }
        breadcrumbs={[{ label: 'Главная', href: '/' }, { label: 'Профиль' }]}
      >
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </PageLayout>
    );
  }

  if (isUserError || !user) {
    return (
      <PageLayout
        title="Профиль"
        titleIcon={
          <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center flex-shrink-0">
            <User className="h-5 w-5 text-white" />
          </div>
        }
        breadcrumbs={[{ label: 'Главная', href: '/' }, { label: 'Профиль' }]}
      >
        <Alert variant="destructive">
          <AlertDescription>Не удалось загрузить профиль. Попробуйте позже.</AlertDescription>
        </Alert>
      </PageLayout>
    );
  }

  return (
    <>
      <PageLayout
        title="Профиль"
        subtitle="Управление личными данными и настройками аккаунта"
        titleIcon={
          <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center flex-shrink-0">
            <User className="h-5 w-5 text-white" />
          </div>
        }
        breadcrumbs={[{ label: 'Главная', href: '/' }, { label: 'Профиль' }]}
      >
        <div className="space-y-6">
          {/* First row: User Info + Security */}
          <div className="grid gap-6 md:grid-cols-2">
            {/* User Information Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="w-5 h-5" />
                  Личные данные
                </CardTitle>
                <CardDescription>Основная информация о вашем аккаунте</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {isEditing ? (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="firstName">Имя</Label>
                      <Input
                        id="firstName"
                        maxLength={150}
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="lastName">Фамилия</Label>
                      <Input
                        id="lastName"
                        maxLength={150}
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button onClick={handleSaveProfile} disabled={updateMutation.isPending}>
                        {updateMutation.isPending ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Save className="w-4 h-4 mr-2" />
                        )}
                        Сохранить
                      </Button>
                      <Button variant="outline" onClick={() => setIsEditing(false)}>
                        Отмена
                      </Button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                        <User className="w-6 h-6 text-blue-600" />
                      </div>
                      <div>
                        <p className="font-medium text-lg">
                          {user.first_name} {user.last_name}
                        </p>
                        <Badge variant={getRoleBadgeVariant(user.role)}>
                          {user.role_display || getRoleDisplayName(user.role)}
                        </Badge>
                      </div>
                    </div>

                    <div className="space-y-3 pt-4 border-t">
                      <div className="flex items-center gap-2 text-gray-600">
                        <Mail className="w-4 h-4" />
                        <span>{user.email}</span>
                      </div>
                      {user.organization && (
                        <div className="flex items-center gap-2 text-gray-600">
                          <Building2 className="w-4 h-4" />
                          <span>{user.organization.name}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-2 text-gray-600">
                        <Shield className="w-4 h-4" />
                        <span>Аккаунт {user.is_active ? 'активен' : 'неактивен'}</span>
                      </div>
                    </div>

                    <Button variant="outline" onClick={() => setIsEditing(true)} className="mt-4">
                      Редактировать
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Security Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Key className="w-5 h-5" />
                  Безопасность
                </CardTitle>
                <CardDescription>Управление паролем и безопасностью аккаунта</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-600">
                    Рекомендуем регулярно менять пароль и использовать сложные комбинации символов.
                  </p>
                </div>

                <Button variant="outline" onClick={() => setShowPasswordDialog(true)}>
                  <Key className="w-4 h-4 mr-2" />
                  Изменить пароль
                </Button>

                <div className="pt-4 border-t">
                  <Button variant="destructive" onClick={handleLogout}>
                    <LogOut className="w-4 h-4 mr-2" />
                    Выйти из аккаунта
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* My Channels (self-service) */}
          {canSeeChannels && (
            <Card data-testid="my-channels-section">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageCircle className="w-5 h-5" />
                  Мои каналы
                </CardTitle>
                <CardDescription>
                  Настройте каналы связи и привяжите Telegram-аккаунт.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {isChannelsLoading ? (
                  <div className="flex items-center justify-center py-6">
                    <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
                  </div>
                ) : fullUser ? (
                  <ChannelsPanel user={fullUser} self />
                ) : (
                  <p className="text-sm text-muted-foreground">
                    Не удалось загрузить каналы.
                  </p>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </PageLayout>

      {/* Change Password Dialog */}
      <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Изменить пароль</DialogTitle>
            <DialogDescription>
              Введите текущий пароль и новый пароль для изменения.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {passwordError && (
              <Alert variant="destructive">
                <AlertDescription>{passwordError}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label htmlFor="currentPassword">Текущий пароль</Label>
              <PasswordInput
                id="currentPassword"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="newPassword">Новый пароль</Label>
              <PasswordInput
                id="newPassword"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Подтвердите новый пароль</Label>
              <PasswordInput
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPasswordDialog(false)}>
              Отмена
            </Button>
            <Button onClick={handleChangePassword} disabled={passwordMutation.isPending}>
              {passwordMutation.isPending ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : null}
              Изменить пароль
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
